import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useLang } from "@/context/LanguageContext";
import { getPartners } from "@/lib/api";
import { Building2 } from "lucide-react";

export const Partners = () => {
  const { t } = useLang();
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    getPartners().then(setPartners).catch(() => setPartners([]));
  }, []);

  const rows = partners.length ? [partners, [...partners].reverse()] : [];

  return (
    <section id="partners" className="relative overflow-hidden bg-[#F7F8FA] py-24 sm:py-28" data-testid="partners">
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 mb-14">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B87B0] mb-5">{t.partners.overline}</p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#090A0C]"
            >
              {t.partners.title}
            </motion.h2>
          </div>
          <p className="lg:col-span-4 text-base sm:text-lg text-[#4B5563] leading-relaxed">{t.partners.lead}</p>
        </div>
      </div>

      {partners.length === 0 ? (
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="rounded-md border border-dashed border-[#E5E7EB] bg-white py-20 text-center" data-testid="partners-empty">
            <Building2 className="mx-auto mb-4 text-[#CBD5E1]" size={40} />
            <p className="text-[#4B5563]">{t.partners.empty}</p>
          </div>
        </div>
      ) : (
        <div className="relative z-10 pt-marquee-fade flex flex-col gap-5">
          {rows.map((row, ri) => (
            <Marquee key={ri} direction={ri % 2 ? "right" : "left"} speed={38} gradient={false} pauseOnHover autoFill>
              {row.map((p) => (
                <PartnerCard key={p.id + ri} p={p} />
              ))}
            </Marquee>
          ))}
        </div>
      )}
    </section>
  );
};

const PartnerCard = ({ p }) => {
  const inner = (
    <div className="group mx-3 flex h-[104px] min-w-[230px] items-center justify-center gap-4 rounded-md border border-[#E5E7EB] bg-white px-10 shadow-[0_6px_24px_rgba(9,10,12,0.04)] transition-colors hover:border-[#00C3FF]" data-testid={`partner-${p.id}`}>
      {p.logo_url ? (
        <img
          src={p.logo_url}
          alt={p.name}
          title={p.name}
          className="max-h-11 w-auto max-w-[160px] object-contain opacity-90 transition-all duration-300 group-hover:opacity-100 group-hover:scale-105"
        />
      ) : (
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-md" style={{ background: "var(--pt-surface-alt)" }}>
            <Building2 size={22} color="var(--pt-blue)" />
          </span>
          <div>
            <div className="font-display font-bold tracking-tight text-[#090A0C]">{p.name}</div>
            {p.category && <div className="text-xs text-[#4B5563] mt-0.5">{p.category}</div>}
          </div>
        </div>
      )}
    </div>
  );
  return p.website ? (
    <a
      href={p.website}
      target="_blank"
      rel="noopener noreferrer"
      title={p.name}
      data-testid={`partner-link-${p.id}`}
      className="block cursor-pointer"
    >
      {inner}
    </a>
  ) : inner;
};
